import React, { useRef } from "react"

const TEXTAREA_CLASS =
  "w-full min-h-[100px] resize-y border border-gray-300 rounded p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
const TEXTAREA_READ_ONLY_CLASS =
  "w-full min-h-[100px] resize-y border border-gray-200 rounded p-2 text-sm bg-gray-50 text-gray-700"
const INPUT_CLASS =
  "w-full border border-gray-300 rounded p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
const INPUT_READ_ONLY_CLASS =
  "w-full border border-gray-200 rounded p-2 text-sm bg-gray-50 text-gray-700"

const DEFAULT_CONFERENCE_FEEDBACK = [
  { strengths: "", competencies: "", growth: "" },
]

export const EMPTY_SITE_VISIT_FORM = {
  visitNumber: "",
  leaderName: "",
  leaderLocation: "",
  administratorRole: "",
  leaderGoalFocus: "",
  cohortType: "",
  meetingPlanContext: "",
  preConferenceNotes: "",
  meetingPlanAttachments: [],
  observationEvidence: "",
  evidenceTellsYou: "",
  patternsTrendsOutliers: "",
  collaborativeReflection: "",
  conferenceFeedback: DEFAULT_CONFERENCE_FEEDBACK,
}

const parseJson = (value, fallback) => {
  if (!value) return fallback
  try {
    const parsed = JSON.parse(value)
    return parsed
  } catch {
    return fallback
  }
}

export const mapSiteVisitToForm = (siteVisit) => {
  const feedback = parseJson(siteVisit?.conferenceFeedback, DEFAULT_CONFERENCE_FEEDBACK)
  const conferenceFeedback =
    feedback.length >= 1 ? [feedback[0]] : DEFAULT_CONFERENCE_FEEDBACK

  return {
    visitNumber: siteVisit?.visitNumber?.toString() || "",
    leaderName: siteVisit?.leaderName || siteVisit?.adminName || "",
    leaderLocation: siteVisit?.leaderLocation || "",
    administratorRole: siteVisit?.administratorRole || "",
    leaderGoalFocus: siteVisit?.leaderGoalFocus || "",
    cohortType: siteVisit?.cohortType || "",
    meetingPlanContext: siteVisit?.meetingPlanContext || "",
    preConferenceNotes: siteVisit?.preConferenceNotes || "",
    meetingPlanAttachments: parseJson(siteVisit?.meetingPlanAttachments, []),
    observationEvidence: siteVisit?.observationEvidence || "",
    evidenceTellsYou: siteVisit?.evidenceTellsYou || "",
    patternsTrendsOutliers: siteVisit?.patternsTrendsOutliers || "",
    collaborativeReflection: siteVisit?.collaborativeReflection || "",
    conferenceFeedback,
  }
}

const FormRow = ({ question, field, value, onChange, readOnly, rows = 5 }) => (
  <tr className="border border-gray-400">
    <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white">
      {question}
    </td>
    <td className="w-[62%] align-top border border-gray-400 p-2 bg-white">
      <textarea
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        readOnly={readOnly}
        rows={rows}
        className={readOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS}
        aria-label={typeof question === "string" ? question : field}
      />
    </td>
  </tr>
)

const COHORT_OPTIONS = [
  { value: "cohort_1", label: "Cohort 1 (Pre-Post- Conference Required)" },
  { value: "cohort_2", label: "Cohort 2 (Post-Conference Required)" },
  {
    value: "additional_site_visit",
    label: "Additional Site Visit (Pre-/Post-Conference Optional)",
  },
]

const SiteVisitForm = ({
  formData,
  onChange,
  mode = "admin",
  status = "",
  adminName,
  onUploadAttachment,
  uploadingAttachment = false,
}) => {
  const fileInputRef = useRef(null)
  const completedByName = adminName || formData.leaderName || "Leader"

  const isPreEditable =
    mode === "admin" &&
    (status === "initiated" || status === "observations_captured")
  const isPostEditable =
    mode === "admin" && status === "observations_captured"

  const isEvidenceEditable =
    mode === "capture_observations" || mode === "super_admin_evaluator"
  const isFeedbackEditable = mode === "super_admin_evaluator"

  const isHeaderEditableOnInitiate = mode === "initiate"
  const isHeaderEditable = mode === "admin" && isPreEditable
  const isHeaderReadOnly = !isHeaderEditableOnInitiate && !isHeaderEditable

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || [])
    if (!files.length || !onUploadAttachment) return

    const oversized = files.find((file) => file.size > 10 * 1024 * 1024)
    if (oversized) {
      alert("Each file must be 10MB or less")
      event.target.value = ""
      return
    }

    await onUploadAttachment(files)
    event.target.value = ""
  }

  const handleRemoveAttachment = (index) => {
    const updated = formData.meetingPlanAttachments.filter((_, i) => i !== index)
    onChange("meetingPlanAttachments", updated)
  }

  const handleFeedbackChange = (rowIndex, field, value) => {
    const updated = formData.conferenceFeedback.map((row, i) =>
      i === rowIndex ? { ...row, [field]: value } : row
    )
    onChange("conferenceFeedback", updated)
  }

  return (
    <div className="space-y-4">
      {adminName && (
        <p className="text-sm text-gray-600">
          Observation/Site Visit for:{" "}
          <span className="font-semibold">{adminName}</span>
        </p>
      )}

      <div className="border border-gray-400 overflow-hidden">
        <div className="bg-[#4472C4] text-center font-bold py-2 border-b border-gray-400 text-sm text-white">
          Leader Evaluation Observation/Site Visit #{" "}
          {formData.visitNumber || "____"}
        </div>

        <table className="w-full border-collapse">
          <tbody>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Name:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="text"
                  value={formData.leaderName}
                  onChange={(e) => onChange("leaderName", e.target.value)}
                  readOnly={isHeaderReadOnly || mode === "initiate"}
                  className={
                    isHeaderReadOnly || mode === "initiate"
                      ? INPUT_READ_ONLY_CLASS
                      : INPUT_CLASS
                  }
                />
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Location:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="text"
                  value={formData.leaderLocation}
                  onChange={(e) => onChange("leaderLocation", e.target.value)}
                  readOnly={isHeaderReadOnly}
                  className={isHeaderReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
                />
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Administrator Role:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="text"
                  value={formData.administratorRole}
                  onChange={(e) => onChange("administratorRole", e.target.value)}
                  readOnly={isHeaderReadOnly}
                  className={isHeaderReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
                />
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Leader Goal/Observation Focus:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <textarea
                  value={formData.leaderGoalFocus}
                  onChange={(e) => onChange("leaderGoalFocus", e.target.value)}
                  readOnly={isHeaderReadOnly}
                  rows={3}
                  className={
                    isHeaderReadOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                  }
                />
              </td>
            </tr>
          </tbody>
        </table>

        <div className="border-t border-gray-400 p-3 bg-white">
          <div className="flex flex-col gap-2">
            {COHORT_OPTIONS.map((option) => (
              <label key={option.value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="cohortType"
                  checked={formData.cohortType === option.value}
                  onChange={() => onChange("cohortType", option.value)}
                  disabled={isHeaderReadOnly}
                  className="h-4 w-4"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        <div className="bg-gray-100 text-center py-2 border-t border-b border-gray-400">
          <div className="font-bold text-sm">Pre-Observation/Visit</div>
          <div className="italic text-sm">
            Completed by {completedByName} (as needed/required)
          </div>
        </div>

        <table className="w-full border-collapse">
          <tbody>
            <FormRow
              question={
                <>
                  Meeting Plan and/or Context:{" "}
                  <span className="italic text-xs block mt-1">
                    Upload and provide hyperlink here, as appropriate
                  </span>
                </>
              }
              field="meetingPlanContext"
              value={formData.meetingPlanContext}
              onChange={onChange}
              readOnly={!isPreEditable}
            />
            <tr className="border border-gray-400">
              <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white font-medium">
                Attachments:
              </td>
              <td className="w-[62%] align-top border border-gray-400 p-3 bg-white">
                {isPreEditable && (
                  <div className="mb-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      aria-label="Upload meeting plan files"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAttachment}
                      className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
                    >
                      {uploadingAttachment ? "Uploading..." : "Upload Attachments"}
                    </button>
                  </div>
                )}
                {formData.meetingPlanAttachments?.length > 0 ? (
                  <ul className="space-y-2">
                    {formData.meetingPlanAttachments.map((file, index) => (
                      <li
                        key={`${file.fileUrl}-${index}`}
                        className="flex items-center justify-between gap-2 text-sm border border-gray-200 rounded p-2"
                      >
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 underline truncate"
                        >
                          {file.fileName}
                        </a>
                        {isPreEditable && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(index)}
                            className="text-red-600 text-xs"
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No attachments uploaded yet.</p>
                )}
              </td>
            </tr>
            <FormRow
              question="Pre-Conference Notes:"
              field="preConferenceNotes"
              value={formData.preConferenceNotes}
              onChange={onChange}
              readOnly={!isPreEditable}
            />
          </tbody>
        </table>

        <div className="bg-gray-100 text-center py-2 border-t border-b border-gray-400">
          <div className="font-bold text-sm">Observation/Site Visit Evidence</div>
          <div className="italic text-sm">Completed by the Evaluator</div>
        </div>

        <div className="border border-gray-400 p-3 bg-white">
          <textarea
            value={formData.observationEvidence}
            onChange={(e) => onChange("observationEvidence", e.target.value)}
            readOnly={!isEvidenceEditable}
            rows={8}
            placeholder={
              !isEvidenceEditable
                ? mode === "admin"
                  ? "Waiting for evaluator to capture Observation/Site Visit Evidence before Post-Observation unlocks"
                  : "To be completed by Super Admin (Evaluator)"
                : "Enter observation / site visit evidence remarks..."
            }
            className={`w-full min-h-[160px] resize-y rounded p-2 text-sm border ${
              !isEvidenceEditable
                ? "bg-gray-50 text-gray-500 border-gray-200"
                : "bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
            }`}
            aria-label="Observation site visit evidence"
          />
        </div>

        <div className="bg-gray-100 text-center py-2 border-t border-b border-gray-400">
          <div className="font-bold text-sm">Post-Observation/Visit Reflection</div>
          <div className="italic text-sm">Completed by {completedByName}</div>
          {mode === "admin" && status === "initiated" && (
            <div className="text-xs text-amber-700 mt-1 px-2">
              Locked until the evaluator adds Observation/Site Visit Evidence remarks
            </div>
          )}
        </div>

        <table className="w-full border-collapse">
          <tbody>
            <FormRow
              question="What does today's evidence tell you?"
              field="evidenceTellsYou"
              value={formData.evidenceTellsYou}
              onChange={onChange}
              readOnly={!isPostEditable}
            />
            <FormRow
              question="Are their patterns, trends, or outliers?"
              field="patternsTrendsOutliers"
              value={formData.patternsTrendsOutliers}
              onChange={onChange}
              readOnly={!isPostEditable}
            />
            <FormRow
              question="How will our collaborative reflection help you move forward and apply your learning in your next steps?"
              field="collaborativeReflection"
              value={formData.collaborativeReflection}
              onChange={onChange}
              readOnly={!isPostEditable}
            />
          </tbody>
        </table>

        <div className="bg-gray-100 text-center py-2 border-t border-b border-gray-400">
          <div className="font-bold text-sm">
            Post-Observation/Visit Conference Feedback
          </div>
          <div className="italic text-sm">Completed by the Evaluator</div>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-400 p-2 text-sm font-medium w-1/3">
                Areas of Strengths
              </th>
              <th className="border border-gray-400 p-2 text-sm font-medium w-1/3">
                Single-Point Competencies
                <div className="italic font-normal text-xs">
                  Completed by the Evaluator
                </div>
              </th>
              <th className="border border-gray-400 p-2 text-sm font-medium w-1/3">
                Areas for Growth and/or Next Steps
              </th>
            </tr>
          </thead>
          <tbody>
            {formData.conferenceFeedback.map((row, rowIndex) => (
              <tr key={`feedback-row-${rowIndex}`} className="border border-gray-400">
                <td className="border border-gray-400 p-2 align-top bg-white">
                  <textarea
                    value={row.strengths}
                    onChange={(e) =>
                      handleFeedbackChange(rowIndex, "strengths", e.target.value)
                    }
                    readOnly={!isFeedbackEditable}
                    rows={4}
                    className={
                      !isFeedbackEditable ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                    }
                    aria-label={`Areas of strengths row ${rowIndex + 1}`}
                  />
                </td>
                <td className="border border-gray-400 p-2 align-top bg-white">
                  <textarea
                    value={row.competencies}
                    onChange={(e) =>
                      handleFeedbackChange(rowIndex, "competencies", e.target.value)
                    }
                    readOnly={!isFeedbackEditable}
                    rows={4}
                    className={
                      !isFeedbackEditable ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                    }
                    aria-label={`Single-point competencies row ${rowIndex + 1}`}
                  />
                </td>
                <td className="border border-gray-400 p-2 align-top bg-white">
                  <textarea
                    value={row.growth}
                    onChange={(e) =>
                      handleFeedbackChange(rowIndex, "growth", e.target.value)
                    }
                    readOnly={!isFeedbackEditable}
                    rows={4}
                    className={
                      !isFeedbackEditable ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                    }
                    aria-label={`Areas for growth row ${rowIndex + 1}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SiteVisitForm
